/**
 * Prerequisite graph utilities. Edges point from a technique to the techniques it requires.
 * All functions are pure so they can run in the API, admin dashboard and mobile app.
 */

export interface PrerequisiteNode {
  id: string;
  prerequisites: readonly string[];
}

export type PrerequisiteGraph = ReadonlyMap<string, readonly string[]>;

export function buildGraph(nodes: readonly PrerequisiteNode[]): Map<string, readonly string[]> {
  return new Map(nodes.map((n) => [n.id, [...n.prerequisites]]));
}

/**
 * Returns the first cycle found as an ordered list of ids (first id repeated at the end),
 * or `null` when the graph is acyclic. Uses iterative DFS so large curricula cannot blow the stack.
 */
export function findCycle(graph: PrerequisiteGraph): string[] | null {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const parent = new Map<string, string>();

  for (const start of graph.keys()) {
    if ((color.get(start) ?? WHITE) !== WHITE) continue;
    const stack: Array<{ id: string; next: number }> = [{ id: start, next: 0 }];
    color.set(start, GREY);

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      const edges = graph.get(frame.id) ?? [];
      if (frame.next >= edges.length) {
        color.set(frame.id, BLACK);
        stack.pop();
        continue;
      }
      const target = edges[frame.next++]!;
      const state = color.get(target) ?? WHITE;
      if (state === GREY) {
        // Walk back from the current node to the target to reconstruct the cycle.
        const cycle = [target];
        let cursor: string | undefined = frame.id;
        while (cursor !== undefined && cursor !== target) {
          cycle.push(cursor);
          cursor = parent.get(cursor);
        }
        cycle.push(target);
        return cycle.reverse();
      }
      if (state === WHITE) {
        color.set(target, GREY);
        parent.set(target, frame.id);
        stack.push({ id: target, next: 0 });
      }
    }
  }
  return null;
}

/**
 * Checks whether replacing `techniqueId`'s prerequisites with `nextPrerequisites` would introduce a
 * cycle (including self-reference). Returns the offending cycle or `null`.
 */
export function wouldCreateCycle(
  graph: PrerequisiteGraph,
  techniqueId: string,
  nextPrerequisites: readonly string[],
): string[] | null {
  if (nextPrerequisites.includes(techniqueId)) return [techniqueId, techniqueId];
  const next = new Map(graph);
  next.set(techniqueId, [...nextPrerequisites]);
  return findCycle(next);
}

/** Prerequisites the student has not yet mastered, in the configured order. */
export function missingPrerequisites(
  prerequisites: readonly string[],
  mastered: ReadonlySet<string>,
  countable?: ReadonlySet<string>,
): string[] {
  return prerequisites.filter((id) => (countable ? countable.has(id) : true) && !mastered.has(id));
}

/** Topologically sorts nodes so every technique follows its prerequisites. Throws on cycles. */
export function topologicalOrder(nodes: readonly PrerequisiteNode[]): string[] {
  const graph = buildGraph(nodes);
  const cycle = findCycle(graph);
  if (cycle) throw new Error(`Prerequisite cycle: ${cycle.join(' -> ')}`);
  const visited = new Set<string>();
  const order: string[] = [];
  const visit = (id: string) => {
    if (visited.has(id)) return;
    visited.add(id);
    for (const dep of graph.get(id) ?? []) visit(dep);
    order.push(id);
  };
  for (const n of nodes) visit(n.id);
  return order;
}
